import { computed, onScopeDispose, ref, type StyleValue } from "vue";

// Gestes verticaux + état déplié d'une feuille basse, animés en hauteur pure (pas de fondu, pas de
// translateY de la feuille pour le repli). Tout le repli/dépli du contenu — geste ET bouton — passe
// par une hauteur animée, ce qui rend l'ouverture et la fermeture symétriques et continues avec le
// doigt :
// - swipe bas, contenu déplié → réduit la hauteur du contenu en suivant le doigt, puis termine le
//   repli (ou ré-étire si sous le seuil).
// - swipe bas, sinon → la feuille suit le doigt en translateY puis se ferme (slide-out) / rebondit.
// - swipe haut depuis la poignée, contenu replié → révèle la hauteur en suivant le doigt, puis
//   termine le dépli (ou referme si sous le seuil).
// - bouton chevron → même animation de hauteur (toggle).

interface UseAppSheetSwipeOptions {
  panelEl: () => HTMLElement | null; // feuille glissée (distance de sortie d'écran, mode dismiss)
  contentEl: () => HTMLElement | null; // contenu repliable (hauteur animée)
  hasContent: () => boolean; // y a-t-il un contenu repliable (présentation structurée) ?
  onClose: () => void; // fermeture effective (l'appelant passe `open` à false)
  threshold?: number; // distance (px) à franchir pour fermer (dismiss), défaut 72
  velocity?: number; // vélocité (px/ms) qui valide même sous le seuil, défaut 0,5
}

const START_SLOP = 6; // mouvement minimal avant d'engager le geste (laisse passer taps et clics)
const ANIM_MS = 250;
const COLLAPSE_FRACTION = 1 / 3; // part de la hauteur à franchir pour valider un repli/dépli

export function useAppSheetSwipe(options: UseAppSheetSwipeOptions) {
  const threshold = options.threshold ?? 72;
  const velocity = options.velocity ?? 0.5;

  const expanded = ref(true); // état logique (chevron / aria)
  const rendered = ref(true); // contenu monté tant que la feuille est ouverte (jamais démonté au repli → mesurable synchronement au dépli)
  const contentH = ref<number | null>(null); // hauteur imposée au contenu (null = naturelle, au repos)
  const dragY = ref(0); // translateY de la feuille (mode dismiss)
  const dragging = ref(false); // doigt en contact : suit le doigt, sans transition
  const settling = ref(false); // animation post-relâchement / toggle en cours
  const mode = ref<"dismiss" | "collapse" | "expand" | null>(null); // verrouillé à l'engagement

  const reduceMotion = () =>
    typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const transMs = () => (reduceMotion() ? 0 : ANIM_MS);

  // Feuille (translateY) en mode dismiss seulement ; sinon la <Transition> d'ouverture/fermeture gère.
  const panelStyle = computed<StyleValue | undefined>(() => {
    if (mode.value !== "dismiss" || (!dragging.value && !settling.value)) return undefined;
    return {
      transform: `translateY(${dragY.value}px)`,
      transition: dragging.value ? "none" : `transform ${transMs()}ms ease-out`,
    };
  });

  // Contenu : hauteur imposée dès qu'on l'anime (overflow caché fige le scroll pendant le geste).
  const contentStyle = computed<StyleValue | undefined>(() => {
    if (contentH.value === null) return undefined;
    return {
      height: `${contentH.value}px`,
      overflow: "hidden",
      transition: dragging.value ? "none" : `height ${transMs()}ms ease-out`,
    };
  });

  let pointerId = -1;
  let startX = 0;
  let startY = 0;
  let lastY = 0;
  let lastT = 0;
  let vy = 0; // vélocité instantanée (px/ms, positif = vers le bas)
  let direction: "down" | "up" | null = null;
  let scroller: HTMLElement | null = null; // scroller source (n'engage le bas qu'à scrollTop 0)
  let allowUp = false;
  let naturalH = 0; // hauteur cible du contenu (repli : courante ; dépli : mesurée)
  let gen = 0; // identifiant de séquence, pour invalider les animations différées dépassées

  // Hauteur du contenu une fois déplié = min(hauteur du contenu, max-height de la classe).
  function expandTarget() {
    const el = options.contentEl();
    if (!el) return 0;
    const maxH = parseFloat(getComputedStyle(el).maxHeight);
    return Number.isFinite(maxH) ? Math.min(el.scrollHeight, maxH) : el.scrollHeight;
  }

  function finishExpand() {
    contentH.value = null;
    expanded.value = true;
    rendered.value = true;
    mode.value = null;
    settling.value = false;
    dragY.value = 0;
  }

  function finishCollapse() {
    contentH.value = 0;
    expanded.value = false;
    mode.value = null;
    settling.value = false;
    dragY.value = 0;
  }

  // Anime contentH (déjà numérique) → target via transition CSS, puis finalise.
  function settleContent(target: number, done: () => void) {
    if (transMs() === 0) {
      contentH.value = target;
      done();
      return;
    }
    const g = gen;
    settling.value = true;
    requestAnimationFrame(() => {
      if (g === gen) contentH.value = target;
    });
    window.setTimeout(() => {
      if (g === gen) done();
    }, transMs());
  }

  function slideOut() {
    if (transMs() === 0) return options.onClose();
    const g = gen;
    const h = options.panelEl()?.offsetHeight ?? window.innerHeight;
    settling.value = true;
    // Glisse la feuille hors écran tant qu'elle est montée (Vue ne patcherait plus son style une fois
    // `open` à false), puis ferme.
    requestAnimationFrame(() => {
      if (g === gen) dragY.value = h;
    });
    window.setTimeout(() => {
      if (g === gen) options.onClose(); // → <Transition> leave → onPanelAfterLeave
    }, transMs());
  }

  function reboundPanel() {
    if (transMs() === 0) {
      mode.value = null;
      dragY.value = 0;
      return;
    }
    const g = gen;
    settling.value = true;
    requestAnimationFrame(() => {
      if (g === gen) dragY.value = 0;
    });
    window.setTimeout(() => {
      if (g === gen) {
        mode.value = null;
        settling.value = false;
        dragY.value = 0;
      }
    }, transMs());
  }

  // Bouton chevron : même animation de hauteur que les gestes.
  function toggle() {
    if (settling.value) return;
    gen++;
    if (expanded.value) {
      contentH.value = options.contentEl()?.offsetHeight ?? 0; // fixe la hauteur courante (auto → px)
      settleContent(0, finishCollapse);
    } else {
      rendered.value = true;
      contentH.value = 0; // monte à 0 puis révèle
      const g = gen;
      requestAnimationFrame(() => {
        if (g === gen) settleContent(expandTarget(), finishExpand);
      });
    }
  }

  // Réouverture de la feuille : état déplié immédiat (la feuille apparaît via la <Transition> externe).
  function resetExpanded() {
    gen++;
    mode.value = null;
    dragging.value = false;
    settling.value = false;
    dragY.value = 0;
    contentH.value = null;
    expanded.value = true;
    rendered.value = true;
  }

  function teardown() {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
    pointerId = -1;
    direction = null;
    scroller = null;
  }

  function begin(e: PointerEvent, fromScroller: HTMLElement | null) {
    if (settling.value) return; // pas de ré-engagement pendant une animation de fin
    if (e.pointerType === "mouse" && e.button !== 0) return;
    gen++;
    pointerId = e.pointerId;
    startX = e.clientX;
    startY = lastY = e.clientY;
    lastT = e.timeStamp;
    vy = 0;
    direction = null;
    scroller = fromScroller;
    allowUp = !fromScroller && options.hasContent() && !expanded.value;
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }

  function onMove(e: PointerEvent) {
    if (e.pointerId !== pointerId) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    if (!direction) {
      if (Math.abs(dy) < START_SLOP && Math.abs(dx) < START_SLOP) return;
      if (Math.abs(dy) <= Math.abs(dx)) return teardown(); // pas assez vertical
      if (dy > 0) {
        if (scroller && scroller.scrollTop > 0) return teardown(); // laisse le scroll natif
        direction = "down";
        if (options.hasContent() && expanded.value) {
          naturalH = options.contentEl()?.offsetHeight ?? 0;
          if (naturalH > 0) {
            mode.value = "collapse";
            contentH.value = naturalH; // fixe la hauteur courante avant de la réduire
          } else {
            mode.value = "dismiss";
          }
        } else {
          mode.value = "dismiss";
        }
      } else {
        if (!allowUp) return teardown(); // pas de suivi haut depuis le contenu (scroll natif)
        direction = "up";
        mode.value = "expand";
        contentH.value = 0;
        naturalH = expandTarget(); // contenu jamais démonté au repli → mesuré ici, bornage dès le 1er frame (pas de débordement)
      }
      dragging.value = true;
    }

    e.preventDefault(); // bloque le scroll/overscroll natif tant que le geste est engagé
    if (direction === "down") {
      dragY.value = Math.max(0, dy);
      if (mode.value === "collapse") contentH.value = Math.max(0, naturalH - dragY.value);
    } else {
      const up = Math.max(0, -dy);
      contentH.value = naturalH > 0 ? Math.min(up, naturalH) : 0;
    }
    const dt = e.timeStamp - lastT;
    if (dt > 0) vy = (e.clientY - lastY) / dt;
    lastY = e.clientY;
    lastT = e.timeStamp;
  }

  function onUp(e: PointerEvent) {
    if (e.pointerId !== pointerId) return;
    const dir = direction;
    const dy = e.clientY - startY;
    teardown();
    if (!dir) return;
    dragging.value = false;

    if (dir === "up") {
      const reached = -dy > naturalH * COLLAPSE_FRACTION || vy < -velocity;
      settleContent(reached ? naturalH : 0, reached ? finishExpand : finishCollapse);
      return;
    }

    if (mode.value === "collapse") {
      const reached = dragY.value > naturalH * COLLAPSE_FRACTION || vy > velocity;
      settleContent(reached ? 0 : naturalH, reached ? finishCollapse : finishExpand);
    } else if (dragY.value > threshold || vy > velocity) {
      slideOut();
    } else {
      reboundPanel();
    }
  }

  // Fin de la leave de la feuille (fermeture) : remet l'état du geste à neutre, sauf si un nouveau
  // geste est déjà engagé.
  function onPanelAfterLeave() {
    if (direction !== null) return;
    mode.value = null;
    dragging.value = false;
    settling.value = false;
    dragY.value = 0;
  }

  onScopeDispose(teardown);

  return {
    expanded,
    rendered,
    panelStyle,
    contentStyle,
    toggle,
    resetExpanded,
    onHandlePointerDown: (e: PointerEvent) => begin(e, null),
    onContentPointerDown: (e: PointerEvent) => begin(e, e.currentTarget as HTMLElement),
    onPanelAfterLeave,
  };
}
